/**
 * SeatOperationRepository.ts
 * 좌석 운영 모드 Repository
 *
 * Features:
 * - 실시간 좌석 현황 조회
 * - 좌석 상세 정보 조회
 * - 순찰 노트 작성/조회
 * - 학생 배정 해제
 */

import { API_BASE_URL } from '../../../config/api';

// ============================================================================
// Types & Interfaces
// ============================================================================

export interface RuntimeSeat {
  seat_id: string;
  seat_number: number;
  seat_code: string;
  x: number;
  y: number;
  width: number;
  height: number;
  rotation: number;
  status: 'occupied' | 'available' | 'reserved' | 'cleaning' | 'inactive';
  zone_id?: string;
  zone_name?: string | null;   // 배치도 구역 (2026-10-04) — 좌석 위쪽 띠·음영으로 구분
  zone_color?: string | null;

  // 현재 학생 정보
  student_id?: string;
  student_name?: string;
  student_phone?: string;
  student_parent_phone?: string;
  student_photo_url?: string;

  // 이용 정보
  check_in_time?: string;
  pure_study_minutes?: number;
  last_activity_time?: string;

  // 실제 출입 상태 (zone_access_logs 기반)
  access_status?: 'entry' | 'exit' | 'outing' | null;

  // 스케줄 상태 정보
  schedule_status?: 'present' | 'absent' | 'out' | 'exited' | 'arriving' | 'not_scheduled' | 'exempt' | 'membership_expired' | 'membership_suspended';
  expected_check_in?: string;
  expected_check_out?: string;
  is_late?: boolean;
  outing_type?: string;
  absence_minutes?: number;
  schedule_never_submitted?: boolean;  // 스케줄을 아예 한 번도 안 낸 학생

  // 외출 복귀 관련
  outing_started_at?: string;       // 외출 시작 시간
  academy_leave_time?: string;     // 학원 출발 시간 (입실 학생용)
  expected_return_time?: string;    // 복귀 예정 시간
  return_overdue_minutes?: number;  // 복귀 지연 시간 (분)

  // 목록 비고용 — 클릭 없이 표시
  exempt_reason?: string;           // 사유서(면제) 사유
  // 🔴 승인 **대기** 사유서. 표시 전용이고 면제가 아니다 — 승인 전 사유서로
  //    면제 처리하면 벌점·미출석 알림톡이 조용히 멈춘다.
  pending_exception?: {
    id?: string | null;             // 사유서 ID — [📮 사유서 확인 요청]에 쓴다
    status?: string | null;         // 'pending'(학부모 승인 전) / 'parent_approved'(원장 승인 전)
    type: string;                   // 결석 / 지각 / 조퇴 / 외출
    time?: string | null;           // 도착·퇴실 예정 시각 (HH:MM)
    reason?: string | null;
    reminded_at?: string | null;    // 오늘 학부모께 확인 요청 보낸 시각 (HH:MM, 하루 1번)
    reminded_by?: string | null;
  };
  last_note?: {                     // 최근 순찰 메모
    id?: string;
    note_type?: string;
    note_text?: string | null;
    created_at?: string;
  };
}

export interface RuntimeFloorLayout {
  floor_id: string;
  floor_name: string;
  canvas_width: number;
  canvas_height: number;
  seats: RuntimeSeat[];
  last_updated: string;
}

export interface SeatDetailInfo {
  seat: RuntimeSeat;
  student?: {
    id: string;
    name: string;
    phone: string;
    email?: string;
    photo_url?: string;
    member_type?: string;
  };
  current_session?: {
    check_in_time: string;
    pure_study_minutes: number;
    last_activity_time: string;
    activity_status: 'studying' | 'away' | 'break';
  };
  recent_notes: PatrolNote[];
}

export interface PatrolNote {
  id: string;
  seat_id: string;
  student_id?: string;
  note_type: 'sleep' | 'off_task' | 'leave' | 'return' | 'other';
  note_content: string;
  created_by: string;
  created_at: string;
}

export interface PatrolNoteCreate {
  seat_id: string;
  note_type: 'sleep' | 'off_task' | 'leave' | 'return' | 'other' | 'focus' | 'normal' | 'phone';
  note_content: string;
  star_effect?: number;  // 별점 영향: +1, 0, -1
  student_id?: string;   // 학생 ID (별점 연동용)
}

// ============================================================================
// Repository Class
// ============================================================================

export class SeatOperationRepository {
  private async apiCall<T>(url: string, options: RequestInit = {}): Promise<T> {
    const token = localStorage.getItem('auth_access_token') || localStorage.getItem('accessToken') || 'demo-token';

    const response = await fetch(`${API_BASE_URL}${url}`, {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
        ...options.headers,
      },
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ detail: '알 수 없는 오류' }));
      throw new Error(errorData.detail || `HTTP ${response.status}`);
    }

    return response.json();
  }

  /**
   * 실시간 좌석 현황 조회
   * GET /api/v1/rooms/{room_id}/runtime-layout
   */
  async getRuntimeLayout(roomId: string): Promise<RuntimeFloorLayout> {
    try {
      const response = await this.apiCall<{
        room: {
          id: string;
          name: string;
          canvas_width: number;
          canvas_height: number;
        };
        seats: Array<{
          id: number;
          seat_code: string;
          seat_number?: number;
          x: number;
          y: number;
          width: number;
          height: number;
          rotation: number;
          seat_type: string;
          zone_id?: string | null;   // 배치도 구역 (2026-10-04)
          zone_name?: string | null;
          zone_color?: string | null;
          is_available: boolean;
          current_student?: {
            id: string;
            name: string;
            phone?: string;
            parent_phone?: string;
            assigned_at?: string;
            access_status?: 'entry' | 'exit' | 'outing' | null;
            outing_type?: string | null;  // 외출 타입
            has_schedule_today?: boolean | null;  // true: 오늘 스케줄 있음, false: 없음, null: 정보 없음
            schedule_never_submitted?: boolean;   // 스케줄을 아예 한 번도 안 낸 학생
            expected_check_in?: string | null;  // 예정 입실 시간 (HH:MM)
            expected_check_out?: string | null;  // 퇴실 예정 시간 (HH:MM)
            academy_leave_time?: string | null;  // 학원 출발 시간 (HH:MM)
            expected_return_time?: string | null;  // 복귀 예정 시간 (HH:MM)
            return_overdue_minutes?: number | null;  // 복귀 지연 시간 (분)
            is_exempt?: boolean;  // 출석 면제 여부
            exempt_reason?: string | null;  // 면제 사유
            pending_exception?: {  // 승인 대기 사유서(표시 전용)
              id?: string | null;
              status?: string | null;
              type: string;
              time?: string | null;
              reason?: string | null;
              reminded_at?: string | null;
              reminded_by?: string | null;
            } | null;
            // 백엔드 계산 schedule_status
            schedule_status?: string | null;
            is_late?: boolean;
            absence_minutes?: number | null;
          };
          last_note?: {
            id: string;
            note_type: string;
            note_text?: string | null;
            created_at: string;
          };
        }>;
        stats: {
          total_seats: number;
          occupied_seats: number;
          available_seats: number;
        };
      }>(`/api/v1/rooms/${roomId}/runtime-layout`);

      // 백엔드 응답을 프론트엔드 형식으로 매핑
      const seats: RuntimeSeat[] = response.seats.map(seat => {
        const accessStatus = seat.current_student?.access_status;

        // 좌석 상태 결정: 백엔드 schedule_status 우선 사용
        let status: RuntimeSeat['status'];
        let scheduleStatus: RuntimeSeat['schedule_status'];

        if (seat.current_student) {
          // 백엔드에서 계산된 schedule_status 사용
          const backendStatus = seat.current_student.schedule_status;

          if (backendStatus) {
            scheduleStatus = backendStatus as RuntimeSeat['schedule_status'];

            // 좌석 상태(status)는 schedule_status에 따라 결정
            if (backendStatus === 'present') {
              status = 'occupied';
            } else if (backendStatus === 'out') {
              status = 'reserved';
            } else {
              status = 'available';
            }
          } else {
            // 백엔드 schedule_status 없으면 (스케줄 없는 학생)
            if (accessStatus === 'entry') {
              status = 'occupied';
              scheduleStatus = 'present';
            } else {
              status = seat.is_available ? 'available' : 'inactive';
              scheduleStatus = undefined;
            }
          }
        } else {
          status = seat.is_available ? 'available' : 'inactive';
          scheduleStatus = undefined;
        }

        return {
          seat_id: String(seat.id),
          seat_number: seat.seat_number || 0,
          seat_code: seat.seat_code || String(seat.seat_number || seat.id),
          zone_id: seat.zone_id || undefined,
          zone_name: seat.zone_name || null,
          zone_color: seat.zone_color || null,
          x: seat.x, // 이미 픽셀 좌표
          y: seat.y,
          width: seat.width || 80,  // CELL_SIZE와 동일 (레이아웃 에디터와 일치)
          height: seat.height || 80, // CELL_SIZE와 동일 (레이아웃 에디터와 일치)
          rotation: seat.rotation || 0,
          status,
          student_id: seat.current_student?.id,
          student_name: seat.current_student?.name,
          student_phone: seat.current_student?.phone,
          student_parent_phone: seat.current_student?.parent_phone,
          check_in_time: seat.current_student?.assigned_at,
          access_status: accessStatus,
          schedule_status: scheduleStatus,
          is_late: seat.current_student?.is_late ?? false,
          absence_minutes: seat.current_student?.absence_minutes ?? undefined,
          schedule_never_submitted: seat.current_student?.schedule_never_submitted ?? false,
          outing_type: seat.current_student?.outing_type ?? undefined,
          expected_check_in: seat.current_student?.expected_check_in ?? undefined,
          expected_check_out: seat.current_student?.expected_check_out ?? undefined,
          academy_leave_time: seat.current_student?.academy_leave_time ?? undefined,
          expected_return_time: seat.current_student?.expected_return_time ?? undefined,
          return_overdue_minutes: seat.current_student?.return_overdue_minutes ?? undefined,
          exempt_reason: seat.current_student?.exempt_reason ?? undefined,
          pending_exception: seat.current_student?.pending_exception ?? undefined,
          last_note: seat.last_note ?? undefined,
        };
      });

      const result: RuntimeFloorLayout = {
        floor_id: response.room.id,
        floor_name: response.room.name,
        canvas_width: response.room.canvas_width,
        canvas_height: response.room.canvas_height,
        seats,
        last_updated: new Date().toISOString(),
      };

      return result;
    } catch (error) {
      console.error('[getRuntimeLayout] error:', error);
      throw error;
    }
  }

  /**
   * 좌석 상세 정보 조회
   * GET /api/v1/seats/{seat_id}/detail
   */
  async getSeatDetail(seatId: string): Promise<SeatDetailInfo> {
    try {

      // 백엔드 응답 형식
      interface BackendSeatDetailResponse {
        seat: {
          id: number;
          floor_id: string;
          floor_name: string;
          seat_code: string;
          seat_number: number;
          x: number;
          y: number;
          width: number;
          height: number;
          rotation: number;
          seat_type: string;
          is_available: boolean;
          // 오늘 승인 대기 사유서(목록과 같은 함수 _get_pending_exception_map)
          pending_exception?: RuntimeSeat['pending_exception'] | null;
        };
        current_assignment: {
          id: string;
          student: {
            id: string;
            name: string;
            phone: string;
            grade?: string;
            study_time_today?: number;
          };
          assigned_at: string;
          status: string;
        } | null;
        patrol_notes: Array<{
          id: string;
          note_type: string;
          description: string;
          created_at: string;
          created_by: { id: string; name: string | null };
        }>;
        note_summary: {
          total_count: number;
          today_count: number;
          by_type: Record<string, number>;
        };
      }

      const response = await this.apiCall<BackendSeatDetailResponse>(
        `/api/v1/seats/${seatId}/detail`
      );

      // 백엔드 응답을 프론트엔드 형식으로 변환
      const result: SeatDetailInfo = {
        seat: {
          seat_id: String(response.seat.id),
          seat_number: response.seat.seat_number,
          seat_code: response.seat.seat_code,
          x: response.seat.x,
          y: response.seat.y,
          width: response.seat.width,
          height: response.seat.height,
          rotation: response.seat.rotation,
          status: response.current_assignment ? 'occupied' : (response.seat.is_available ? 'available' : 'inactive'),
          // 🔴 2026-10-27: 이 줄이 빠져 있어 9/19 에 넣은 '⏳ 사유서 제출됨(승인 전)' 안내가
          //    좌석 상세에 한 번도 안 떴다(서버는 내려주고 있었음). 사유서 확인 요청 버튼도 이 값을 쓴다.
          pending_exception: response.seat.pending_exception ?? undefined,
        },
        student: response.current_assignment ? {
          id: response.current_assignment.student.id,
          name: response.current_assignment.student.name,
          phone: response.current_assignment.student.phone,
        } : undefined,
        current_session: response.current_assignment ? {
          check_in_time: response.current_assignment.assigned_at,
          pure_study_minutes: response.current_assignment.student.study_time_today || 0,
          last_activity_time: response.current_assignment.assigned_at,
          activity_status: 'studying' as const,
        } : undefined,
        recent_notes: response.patrol_notes.map(note => ({
          id: note.id,
          seat_id: seatId,
          note_type: note.note_type as PatrolNote['note_type'],
          note_content: note.description,
          created_by: note.created_by?.id || '',
          created_at: note.created_at,
        })),
      };

      return result;
    } catch (error) {
      console.error('❌ [getSeatDetail] 실패:', error);
      throw error;
    }
  }

  /**
   * 순찰 노트 작성
   * POST /api/v1/seats/{seat_id}/notes
   */
  async createPatrolNote(seatId: string, data: PatrolNoteCreate): Promise<PatrolNote> {
    try {
      const result = await this.apiCall<PatrolNote>(
        `/api/v1/seats/${seatId}/notes`,
        {
          method: 'POST',
          body: JSON.stringify(data),
        }
      );
      return result;
    } catch (error) {
      console.error('❌ [createPatrolNote] 실패:', error);
      throw error;
    }
  }

  /**
   * 학생 배정 해제
   * POST /api/v1/seats/{seat_id}/unassign
   */
  async unassignStudent(seatId: string): Promise<void> {
    try {
      await this.apiCall<void>(
        `/api/v1/seats/${seatId}/unassign`,
        {
          method: 'POST',
        }
      );
    } catch (error) {
      console.error('❌ [unassignStudent] 실패:', error);
      throw error;
    }
  }
}

export const seatOperationRepository = new SeatOperationRepository();
